import React from 'react';
import Box from './Layout/Box';
import AppText from './AppText';
import CustomTaskDoneCheckbox from './CustomTaskDoneCheckbox';
import {t} from '../../services/i18n';
import {Colors} from '../../styles/Colors';
import {GlobalStyleAttributes} from '../../styles/GlobalStyles';

const COLUMN_WIDTH = 80;
const NAME_COLUMN_MIN_WIDTH = 502;
const TABLE_HEADER_HEIGHT = 45;
const TABLE_ROW_HEIGHT = 53;
const TABLE_BORDER_COLOR = '#E0E0E0';
const TABLE_HEADER_BG = '#f0f0f0';
const TABLE_GROUP_BG = '#f9f9f9';
const TABLE_CELL_PADDING_INLINE = 20;
const TABLE_GROUP_PADDING = '8px 20px 9px';
const TABLE_ROW_PADDING_BLOCK = 16;

const CHECKBOX_COLORS = {
    primary: Colors.newDesign.mainBlue,
    primaryBorder: TABLE_BORDER_COLOR,
    primaryHover: Colors.newDesign.mainBlueHover,
};

const cellBorderInlineStart = {borderInlineStart: `1px solid ${TABLE_BORDER_COLOR}`};
const cellBorderBottom = {borderBottom: `1px solid ${TABLE_BORDER_COLOR}`};

const getGridTemplateColumns = (columnCount) =>
    `minmax(${NAME_COLUMN_MIN_WIDTH}px, 1fr) repeat(${columnCount}, minmax(0, 1fr))`;

const columnCellStyle = {
    minWidth: 0,
    overflow: 'hidden',
};

const CheckboxCell = ({columnKey, isSelected, isDisabled, disabledTooltipKey, onSelect}) => (
    <Box
        style={{
            ...columnCellStyle,
            ...cellBorderInlineStart,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: TABLE_ROW_HEIGHT,
        }}
    >
        <CustomTaskDoneCheckbox
            checked={isSelected}
            disabled={isDisabled}
            onClick={(event) => event.stopPropagation()}
            checkedTooltip={isDisabled ? t(disabledTooltipKey) : ''}
            uncheckedTooltip={isDisabled ? t(disabledTooltipKey) : ''}
            colors={CHECKBOX_COLORS}
            onChange={(event) => {
                if (isDisabled) {
                    return;
                }
                if (event.target.checked) {
                    onSelect(columnKey);
                } else if (isSelected) {
                    onSelect(columnKey);
                }
            }}
        />
    </Box>
);

const GroupedCheckboxTable = ({
    nameColumnLabelKey,
    columns,
    groups,
    emptyMessageKey,
    disabledTooltipKey,
    onSelect,
}) => {
    const gridTemplateColumns = getGridTemplateColumns(columns.length);
    const tableMinWidth = NAME_COLUMN_MIN_WIDTH + (COLUMN_WIDTH * columns.length);

    return (
        <Box
            width={'100%'}
            style={{
                display: 'block',
                border: `1px solid ${TABLE_BORDER_COLOR}`,
                borderRadius: '10px',
                overflow: 'hidden',
            }}
        >
            <Box overflowX={'auto'} width={'100%'} style={{display: 'block'}}>
                <Box minWidth={`${tableMinWidth}px`} style={{display: 'block'}}>
                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns,
                            backgroundColor: TABLE_HEADER_BG,
                            height: TABLE_HEADER_HEIGHT,
                            ...cellBorderBottom,
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingInlineStart: TABLE_CELL_PADDING_INLINE,
                            }}
                        >
                            <AppText fontSize={'14px'} mediumFont color={Colors.newDesign.mainText}>
                                {t(nameColumnLabelKey)}
                            </AppText>
                        </Box>
                        {columns.map(column => (
                            <Box
                                key={column.key}
                                style={{
                                    ...columnCellStyle,
                                    ...cellBorderInlineStart,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <AppText
                                    fontSize={'14px'}
                                    mediumFont
                                    color={Colors.newDesign.mainText}
                                    style={{
                                        textAlign: 'center',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                                                        
                                    }}
                                >
                                    {column.labelKey ? t(column.labelKey) : column.label}
                                </AppText>
                            </Box>
                        ))}
                    </Box>

                    {groups.length === 0 ? (
                        <Box padding={'2rem 1.25rem'} style={{display: 'block', ...cellBorderBottom}}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} color={Colors.newDesign.darkGrey}>
                                {t(emptyMessageKey)}
                            </AppText>
                        </Box>
                    ) : groups.map(group => (
                        <Box key={group.key} width={'100%'} style={{display: 'block'}}>
                            <Box
                                style={{
                                    display: 'block',
                                    backgroundColor: TABLE_GROUP_BG,
                                    padding: TABLE_GROUP_PADDING,
                                    ...cellBorderBottom,
                                }}
                            >
                                <AppText
                                    fontSize={'12px'}
                                    mediumFont
                                    color={Colors.newDesign.mainText}
                                    style={{letterSpacing: '0.3px', lineHeight: '16px'}}
                                >
                                    {group.title}
                                </AppText>
                            </Box>
                            {group.rows.map(row => (
                                <Box
                                    key={row.key}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns,
                                        height: TABLE_ROW_HEIGHT,
                                        ...cellBorderBottom,
                                    }}
                                >
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingBlock: TABLE_ROW_PADDING_BLOCK,
                                            paddingInlineStart: TABLE_CELL_PADDING_INLINE,
                                            paddingInlineEnd: TABLE_CELL_PADDING_INLINE,
                                        }}
                                    >
                                        <AppText fontSize={'14px'} color={'#101828'} style={{lineHeight: '20px'}}>
                                            {row.label}
                                        </AppText>
                                    </Box>
                                    {columns.map(column => (
                                        <CheckboxCell
                                            key={`${row.key}-${column.key}`}
                                            columnKey={column.key}
                                            isSelected={row.selectedColumnKey === column.key}
                                            isDisabled={row.isColumnDisabled?.(column.key) ?? false}
                                            disabledTooltipKey={disabledTooltipKey}
                                            onSelect={(value) => onSelect(row.key, value)}
                                        />
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default GroupedCheckboxTable;
